// The one createApi instance for the whole app — a single reducerPath keeps
// one unified cache and lets tag invalidation work across feature
// boundaries. Each feature's own api/*.api.ts file calls
// baseApi.injectEndpoints(...) against this instance rather than creating
// its own createApi — see docs/state-management.md.

import { createApi, type BaseQueryFn } from "@reduxjs/toolkit/query/react";
import { deleteJson, getJson, postJson, putJson, ApiError } from "@/app/apiClient";

export interface QueryApiError {
  status: number;
  code: string | null;
  message: string;
}

export function isQueryApiError(error: unknown): error is QueryApiError {
  return typeof error === "object" && error !== null && "message" in error && "status" in error;
}

export interface ApiRequestArgs {
  url: string;
  method?: "GET" | "POST" | "PUT" | "DELETE";
  body?: unknown;
  auth?: boolean;
  // Opts a specific endpoint out of apiClient's default auto-logout-on-401 —
  // see apiClient.ts's own RequestOptions for why this exists (a legitimate
  // 401 business outcome, not a bad/expired token).
  skipAuthExpiryOn401?: boolean;
}

// Thin adapter over apiClient.ts's existing getJson/postJson/putJson/deleteJson
// — deliberately not reimplemented here. The X-Tenant-Id header, Bearer auth,
// ApiError envelope parsing, and 401 -> lendcord:session-expired event all
// come from that one shared code path, so they don't need separate testing
// for the RTK Query call path vs. anything still calling apiClient directly.
const rawBaseQuery: BaseQueryFn<ApiRequestArgs, unknown, QueryApiError> = async (args) => {
  try {
    const { url, method = "GET", body, auth, skipAuthExpiryOn401 } = args;
    const data =
      method === "GET"
        ? await getJson(url, { auth })
        : method === "DELETE"
          ? await deleteJson(url, body, { auth })
          : method === "PUT"
            ? await putJson(url, body, { auth })
            : await postJson(url, body, { auth, skipAuthExpiryOn401 });
    return { data };
  } catch (error) {
    if (error instanceof ApiError) {
      return { error: { status: error.status, code: error.code, message: error.message } };
    }
    return { error: { status: 0, code: null, message: (error as Error).message } };
  }
};

export const baseApi = createApi({
  reducerPath: "api",
  baseQuery: rawBaseQuery,
  tagTypes: [
    "Profile",
    "RoleUpgradeRequest",
    "OnboardingApplication",
    "Branch",
    "LoanScheme",
    "EnterpriseBrandProfile",
    "PeopleDirectory",
    "JoinEnterpriseRequest",
    "CreditorOverview",
    "Investor",
    "EmiSummary",
    "EmiRecord",
    "Borrower",
    "Portfolio",
    "Report",
    "WalletSummary",
  ],
  endpoints: () => ({}),
});
